import React, { useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import TenantBrandingColorField, {
  normalizeBrandingColorForApi,
} from 'shared/components/TenantBrandingColorField';
import TenantLogoUploadField from 'shared/components/TenantLogoUploadField';
import { uploadTenantRegistryLogo } from 'shared/api/tenantLogoUpload';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';

const STATUS_OPTIONS = ['ACTIVE', 'DISABLED', 'DELETED', 'PENDING'];

const defaultInitial = {
  name: '',
  url: '',
  username: '',
  password: '',
  driverClassName: 'com.mysql.cj.jdbc.Driver',
  initialize: true,
  status: 'ACTIVE',
  primaryColor: '',
  secondaryColor: '',
  backgroundColor: '',
  tenantPhoto: '',
};

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required'),
  url: yup.string().trim().when('initialize', {
    is: false,
    then: (schema) => schema.required('JDBC URL is required when not auto-provisioning'),
    otherwise: (schema) => schema,
  }),
  username: yup.string().trim().when('initialize', {
    is: false,
    then: (schema) => schema.required('Username is required when not auto-provisioning'),
    otherwise: (schema) => schema,
  }),
  password: yup.string().when('initialize', {
    is: false,
    then: (schema) => schema.required('Password is required when not auto-provisioning'),
    otherwise: (schema) => schema,
  }),
  driverClassName: yup.string().trim().when('initialize', {
    is: false,
    then: (schema) => schema.required('Driver class is required when not auto-provisioning'),
    otherwise: (schema) => schema,
  }),
  initialize: yup.boolean(),
  status: yup.string().required('Status is required'),
  primaryColor: yup.string().trim().max(64),
  secondaryColor: yup.string().trim().max(64),
  backgroundColor: yup.string().trim().max(64),
  tenantPhoto: yup.string().trim().max(2048),
});

const CreateTenantDialog = ({ open, onClose, onSuccess, createTenant, updateTenant }) => {
  const [submitting, setSubmitting] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      const trimOrNull = (v) => {
        const t = v == null ? '' : String(v).trim();
        return t === '' ? null : t;
      };
      const autoProvision = Boolean(values.initialize);
      const payload = {
        name: values.name.trim(),
        initialize: autoProvision,
        status: values.status,
        primaryColor: normalizeBrandingColorForApi(values.primaryColor) || null,
        secondaryColor: normalizeBrandingColorForApi(values.secondaryColor) || null,
        backgroundColor: normalizeBrandingColorForApi(values.backgroundColor) || null,
        tenantPhoto: pendingLogoFile ? null : trimOrNull(values.tenantPhoto),
      };
      if (!autoProvision) {
        payload.url = values.url.trim();
        payload.username = values.username.trim();
        payload.password = values.password;
        payload.driverClassName = values.driverClassName.trim();
      }
      const created = await createTenant(payload);
      const tenantId = created?.id;

      if (pendingLogoFile && tenantId) {
        const photoPath = await uploadTenantRegistryLogo(pendingLogoFile, tenantId);
        if (photoPath && updateTenant) {
          await updateTenant(tenantId, {
            ...created,
            tenantPhoto: photoPath,
          });
        }
      }

      toast.success('Tenant created successfully');
      resetForm();
      setPendingLogoFile(null);
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to create tenant';
      toast.error(typeof err === 'string' ? err : 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          width: '100%',
          pb: 1,
        }}
      >
        Create tenant
      </DialogTitle>
      <Formik
        initialValues={defaultInitial}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <DialogContent sx={{ position: 'relative', pt: 0 }}>
              {submitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255,255,255,0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <ClockLoader inline size={22} />
                </Box>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <TextField
                  fullWidth
                  margin="dense"
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                {!values.initialize && (
                  <TextField
                    fullWidth
                    margin="dense"
                    name="username"
                    label="Username"
                    value={values.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.username && Boolean(errors.username)}
                    helperText={touched.username && errors.username}
                  />
                )}
                {!values.initialize && (
                  <>
                    <TextField
                      fullWidth
                      margin="dense"
                      name="url"
                      label="JDBC URL"
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.url && Boolean(errors.url)}
                      helperText={touched.url && errors.url}
                      sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      name="password"
                      label="Password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      name="driverClassName"
                      label="Driver class name"
                      value={values.driverClassName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.driverClassName && Boolean(errors.driverClassName)}
                      helperText={touched.driverClassName && errors.driverClassName}
                    />
                  </>
                )}
                <FormControl fullWidth margin="dense" sx={{ minWidth: 0 }}>
                  <InputLabel id="tenant-status-label">Status</InputLabel>
                  <Select
                    labelId="tenant-status-label"
                    name="status"
                    label="Status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.status && Boolean(errors.status)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 56,
                    pt: { xs: 0, sm: 1 },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.initialize}
                        onChange={(_, v) => setFieldValue('initialize', v)}
                        name="initialize"
                      />
                    }
                    label="Auto-create database and tables (recommended)"
                  />
                </Box>

                {values.initialize && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ gridColumn: '1 / -1' }}
                  >
                    A MySQL database named tenant_&lt;name&gt; will be created and migrated
                    automatically using the same server credentials as the central registry.
                  </Typography>
                )}
                <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />
                <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1' }}>
                  Branding (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', mt: -0.5 }}>
                  Hover a color field or click the swatch to open presets; the hex code is saved.
                </Typography>
                <TenantBrandingColorField
                  name="primaryColor"
                  label="Primary color"
                  placeholder="#1976d2"
                  value={values.primaryColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.primaryColor && errors.primaryColor}
                  helperText={
                    (touched.primaryColor && errors.primaryColor) || 'Preset or custom (#RRGGBB)'
                  }
                />
                <TenantBrandingColorField
                  name="secondaryColor"
                  label="Secondary color"
                  placeholder="#ffffff"
                  value={values.secondaryColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.secondaryColor && errors.secondaryColor}
                  helperText={touched.secondaryColor && errors.secondaryColor}
                />
                <TenantBrandingColorField
                  name="backgroundColor"
                  label="Background color"
                  placeholder="#f5f5f5"
                  value={values.backgroundColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.backgroundColor && errors.backgroundColor}
                  helperText={touched.backgroundColor && errors.backgroundColor}
                />
                <TenantLogoUploadField
                  value={values.tenantPhoto}
                  setFieldValue={setFieldValue}
                  registryTenantId={null}
                  deferredUpload
                  pendingFile={pendingLogoFile}
                  onPendingFileChange={setPendingLogoFile}
                  disabled={submitting}
                  error={touched.tenantPhoto && errors.tenantPhoto}
                  helperText="Choose a logo, then click Create — the image uploads after the tenant is created."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                Create
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateTenantDialog;
